import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { BODY_SMALL } from "@/components/marketing/marketingType";
import { MID, TINY, marketingPalette } from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { useNeighbours } from "../LandingPageContext";
import {
  CARD_FRAME,
  CardBody,
  CardGrid,
  CardTitle,
  CheckLine,
  LandingButton,
  LandingImage,
  LandingSection,
  SectionHeading,
} from "../LandingPrimitives";
import CardsAside from "./CardsAside";
import CardsBenefits from "./CardsBenefits";
import CardsCompare from "./CardsCompare";
import CardsFeature from "./CardsFeature";
import ProductRows from "./cardsProductRows";
import CardsProduct from "./CardsProduct";
import CardsSteps from "./CardsSteps";
import CardsTone from "./CardsTone";
import CardsVisual from "./CardsVisual";
import type { Cards } from "./cardsShared";
import { RUN_RAIL, continuesRun, runFoot, sectionFoot } from "./landingRun";
import { Lines } from "@/components/marketing/Lines";

/** What each of the remaining card variants draws its artwork at. */
function artworkSx(variant: Cards["variant"]): SxProps<Theme> {
  switch (variant) {
    case "logo":
      return { width: "34px", height: "34px", flexShrink: 0 };
    case "plain":
      return { width: "48px", height: "48px", objectFit: "cover" };
    default:
      return { width: "40px", height: "40px" };
  }
}

/** `companies-item`: the competitor rundown is a ruled table, not a row of cards. */
const LOGO_CELL = {
  p: "40px",
  gap: "20px",
  border: "none",
  borderRadius: 0,
  backgroundColor: "transparent",
  // `.benefits_body`: the rundown's line is 14 on 160% in the page's ink,
  // faded to 70%.
  "& p": {
    fontSize: 14,
    lineHeight: "22.4px",
    color: marketingPalette.ink,
    opacity: 0.7,
  },
  "&:nth-of-type(2n+1)": {
    borderRight: `1px solid ${marketingPalette.outline}`,
  },
  "&:nth-of-type(n+3)": {
    borderTop: `1px solid ${marketingPalette.outline}`,
  },
  [MID]: { p: "24px" },
  [TINY]: {
    "&:nth-of-type(2n+1)": { borderRight: "none" },
    "&:nth-of-type(n+3)": { borderTop: "none" },
    borderBottom: `1px solid ${marketingPalette.outline}`,
  },
} satisfies SxProps<Theme>;

/**
 * `.alter-pricing_item`: the comparison pages' price card, a tint with no rule
 * around it and a wider step between its parts than the framed cards keep.
 */
const PRICE_CARD = {
  gap: "20px",
  p: "20px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: marketingPalette.surface,
} satisfies SxProps<Theme>;

/** `caps-txt`: a caption under a price card, not a headline number. */
function PriceCaption({ price }: { price: string }) {
  return (
    <Box
      sx={{
        fontSize: 12.96,
        fontWeight: 500,
        lineHeight: "19.44px",
        textTransform: "uppercase",
      }}
    >
      {price}
    </Box>
  );
}

/** The artwork and title, which the competitor rundown sets on one line. */
function CardHead({
  card,
  variant,
}: {
  card: Card;
  variant: Cards["variant"];
}) {
  const title = (
    // `companies-item_logo`'s title is an `h6`.
    <CardTitle tracked={variant === "logo"}>
      <Lines text={card.title} />
    </CardTitle>
  );
  if (variant === "logo" && card.icon) {
    // `companies-item_top`: the logo sits on the title's own line here.
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <LandingImage src={card.icon} sx={artworkSx(variant)} />
        {title}
      </Box>
    );
  }
  return (
    <>
      {card.icon ? (
        <LandingImage src={card.icon} sx={artworkSx(variant)} />
      ) : null}
      {card.image ? (
        <LandingImage
          src={card.image}
          sx={{ width: 1, height: "auto", borderRadius: "8px" }}
        />
      ) : null}
      {card.label ? (
        <Typography sx={[BODY_SMALL, { color: marketingPalette.inkFaint }]}>
          {card.label}
        </Typography>
      ) : null}
      {title}
    </>
  );
}

/** As in `SplitBlock`, a card's list answers its first paragraph. */
function CardCopy({ card }: { card: Card }) {
  const [lead, ...rest] = card.paragraphs ?? [];
  return (
    <>
      {card.body ? <CardBody>{card.body}</CardBody> : null}
      {lead ? <CardBody>{lead}</CardBody> : null}
      {card.bullets?.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {card.bullets.map((bullet, index) => (
            <CheckLine key={index} label={bullet} />
          ))}
        </Box>
      ) : null}
      {rest.map((paragraph, index) => (
        <CardBody key={index}>{paragraph}</CardBody>
      ))}
    </>
  );
}

/** What a card closes on: a caption, a numeral, or a control. */
function CardFoot({ card }: { card: Card }) {
  return (
    <>
      {card.price ? <PriceCaption price={card.price} /> : null}
      {card.step ? (
        <Typography sx={[BODY_SMALL, { color: marketingPalette.inkFaint }]}>
          Step {card.step}
        </Typography>
      ) : null}
      {card.cta ? (
        <Box sx={{ mt: "auto", pt: "8px", display: "flex" }}>
          <LandingButton cta={card.cta} variant="secondary" />
        </Box>
      ) : null}
    </>
  );
}

/** `.alter-pricing_item`: the picture, then its title and line 8 apart, then the caption. */
function PriceFace({ card }: { card: Card }) {
  return (
    <Box sx={[CARD_FRAME, PRICE_CARD]}>
      {card.image ? (
        <LandingImage
          src={card.image}
          sx={{ width: 1, height: "auto", borderRadius: "8px" }}
        />
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <CardTitle tracked>
          <Lines text={card.title} />
        </CardTitle>
        {card.body ? <CardBody>{card.body}</CardBody> : null}
      </Box>
      {card.price ? <PriceCaption price={card.price} /> : null}
    </Box>
  );
}

function CardFace({
  card,
  variant,
}: {
  card: Card;
  variant: Cards["variant"];
}) {
  if (variant === "price") {
    return <PriceFace card={card} />;
  }
  return (
    <Box sx={[CARD_FRAME, variant === "logo" ? LOGO_CELL : {}]}>
      <CardHead card={card} variant={variant} />
      <CardCopy card={card} />
      <CardFoot card={card} />
    </Box>
  );
}

/** Two across for the competitor rundown, three for the price strip, four for the rest. */
function columnsFor(block: Cards): number {
  const count = block.items.length;
  if (block.across) {
    return Math.min(count, block.across);
  }
  if (block.variant === "logo") {
    return Math.min(count, 2);
  }
  if (block.variant === "price") {
    return Math.min(count, 3);
  }
  return Math.min(count, 4);
}

/**
 * The published `benefits_section`, which three of the variants draw: the
 * feature grid, the one `tone` block whose cards carry no tint, and the one
 * `visual` block whose cards are pictures under a centred title.
 */
function isBenefitsBand(block: Cards): boolean {
  if (block.variant === "plain") {
    return !block.items.some((card) => card.step);
  }
  if (block.variant === "tone") {
    return false;
  }
  return (
    block.variant === "visual" &&
    block.items.every((card) => card.image !== undefined && !card.body)
  );
}

/** The feature panels, or the paid-search page's product rows stacked in the cloneable. */
function featureBand(block: Cards, continues: boolean) {
  return block.productRows ? (
    <ProductRows block={block} />
  ) : (
    <CardsFeature block={block} continues={continues} />
  );
}

/** The bands with a shape of their own; anything else falls to the card grid. */
function bandFor(block: Cards, continues: boolean) {
  if (block.aside) {
    return <CardsAside block={block} />;
  }
  if (isBenefitsBand(block)) {
    return <CardsBenefits block={block} continues={continues} />;
  }
  if (block.variant === "plain") {
    // Not a benefits band, so its cards carry steps: the numbered strip.
    return <CardsSteps block={block} />;
  }
  switch (block.variant) {
    case "tone":
      return <CardsTone block={block} />;
    case "visual":
      return <CardsVisual block={block} />;
    case "feature":
      return featureBand(block, continues);
    case "product":
      return <CardsProduct block={block} continues={continues} />;
    case "compare":
      return <CardsCompare block={block} />;
    default:
      return null;
  }
}

/**
 * The card grid under a centred heading. A step of a run (the comparison
 * pages' price cards, and the competitor rundown under its own heading) is
 * set on the run's measure with the published `.alter-pricing_wrap` step
 * between its parts, and closes on the run's gutter rather than the break.
 */
export default function CardsBlock({
  block,
  continues = false,
}: {
  block: Cards;
  /** True when this block continues the section above rather than opening one. */
  continues?: boolean;
}) {
  const { next } = useNeighbours(block);
  const band = bandFor(block, continues);
  if (band) {
    return band;
  }
  const step = continuesRun(block);
  return (
    <LandingSection {...sectionFoot(runFoot(next))}>
      <Box
        sx={[
          { display: "flex", flexDirection: "column", gap: "32px" },
          step ? { ...RUN_RAIL, gap: "24px" } : null,
        ]}
      >
        <SectionHeading
          title={block.title}
          parts={block.titleParts}
          body={block.body}
          links={block.links}
          level={block.level === 3 ? "h3" : "h2"}
          align={block.headingAlign ?? "center"}
          bodyTone={block.bodyTone}
          hoverLinks={block.hoverLinks}
        />
        <CardGrid
          columns={columnsFor(block)}
          gap={gapFor(block)}
          stackAtMid={block.variant === "price"}
        >
          {block.items.map((card, index) => (
            <CardFace key={index} card={card} variant={block.variant} />
          ))}
        </CardGrid>
        {block.note ? (
          <Typography
            component="em"
            sx={{
              display: "block",
              textAlign: "center",
              fontStyle: "italic",
              fontSize: 16,
              lineHeight: "24px",
              color: marketingPalette.ink,
            }}
          >
            {block.note}
          </Typography>
        ) : null}
      </Box>
    </LandingSection>
  );
}

/** The competitor rundown rules its cells rather than spacing them. */
function gapFor(block: Cards): string | undefined {
  return block.variant === "logo" ? "0px" : undefined;
}
